import { io, type Socket } from 'socket.io-client';

export interface MultiplayerOptions {
  serverUrl: () => string;
  createSocket?: typeof io;
  log?: (message: string, context?: Record<string, unknown>) => void;
  /** Successful admission events and events that revoke membership. */
  membership?: { joined: readonly string[]; left: readonly string[] };
  /** Optional application heartbeat; Socket.IO also has its own transport heartbeat. */
  heartbeat?: {
    ping: string;
    pong: string;
    intervalMs: number;
    timeoutMs: number;
    maxMissed: number;
  };
}

export interface AcknowledgementOptions {
  successEvent: string;
  errorEvents?: readonly string[];
  timeoutMs?: number;
  requireMembership?: boolean;
}

/** Socket.IO connection lifecycle with explicit, non-buffered command delivery.
 * Commands maps event names to argument tuples, e.g. { move: [column: number] }.
 */
export class MultiplayerClient<Commands extends { [K in keyof Commands]: unknown[] }> {
  private socket: Socket | null = null;
  private joined = false;
  private heartbeatInterval: ReturnType<typeof setInterval> | undefined;
  private heartbeatTimeout: ReturnType<typeof setTimeout> | undefined;
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  private missedHeartbeats = 0;
  private pending = new Map<string, () => void>();

  constructor(private readonly options: MultiplayerOptions) {
    const heartbeat = options.heartbeat;
    if (heartbeat && (!Number.isFinite(heartbeat.intervalMs) || heartbeat.intervalMs <= 0 ||
      !Number.isFinite(heartbeat.timeoutMs) || heartbeat.timeoutMs <= 0 ||
      heartbeat.timeoutMs >= heartbeat.intervalMs || !Number.isInteger(heartbeat.maxMissed) || heartbeat.maxMissed < 1)) {
      throw new Error('Heartbeat requires positive times, timeout < interval, and maxMissed >= 1');
    }
  }

  connect(): Socket {
    if (this.socket) return this.socket;
    const socket = (this.options.createSocket || io)(this.options.serverUrl(), {
      autoConnect: false,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });
    this.socket = socket;
    socket.on('connect_error', error => this.options.log?.('Connection failed', { message: error.message }));
    socket.on('disconnect', reason => {
      this.joined = false;
      this.stopHeartbeat();
      this.cancelPending();
      this.options.log?.('Socket disconnected', { reason });
    });
    socket.on('connect', () => {
      this.cancelReconnect();
      this.joined = false;
      this.missedHeartbeats = 0;
      this.startHeartbeat();
      this.options.log?.('Socket connected successfully');
    });
    for (const event of this.options.membership?.joined || []) socket.on(event, () => { this.joined = true; });
    for (const event of this.options.membership?.left || []) socket.on(event, () => { this.joined = false; });
    if (this.options.heartbeat) socket.on(this.options.heartbeat.pong, () => {
      this.missedHeartbeats = 0;
      clearTimeout(this.heartbeatTimeout);
      this.heartbeatTimeout = undefined;
    });
    socket.connect();
    return socket;
  }

  getSocket() { return this.socket; }
  isReady() { return !!this.socket?.connected && (!this.options.membership || this.joined); }

  /** Returns false instead of buffering an offline or pre-admission action. */
  send<K extends keyof Commands & string>(event: K, args: Commands[K], requireMembership = true): boolean {
    if (!this.socket?.connected || (requireMembership && !this.isReady())) return false;
    this.socket.emit(event, ...args);
    return true;
  }

  /** For protocols acknowledging commands with events rather than Socket.IO callbacks.
   * Only one outstanding command per success event is allowed (no correlation ID).
   * A timeout means unconfirmed; it does not prove the server rejected the command.
   */
  sendAndWait<K extends keyof Commands & string>(event: K, args: Commands[K], options: AcknowledgementOptions): Promise<boolean> {
    const socket = this.socket;
    const timeoutMs = options.timeoutMs ?? 10000;
    if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) throw new Error('Acknowledgement timeout must be positive');
    if (!socket?.connected || (options.requireMembership !== false && !this.isReady()) || this.pending.has(options.successEvent)) {
      return Promise.resolve(false);
    }
    return new Promise(resolve => {
      const finish = (success: boolean) => {
        clearTimeout(timeout);
        socket.off(options.successEvent, onSuccess);
        for (const error of options.errorEvents || []) socket.off(error, onFailure);
        this.pending.delete(options.successEvent);
        resolve(success);
      };
      const onSuccess = () => finish(true);
      const onFailure = () => finish(false);
      const timeout = setTimeout(onFailure, timeoutMs);
      this.pending.set(options.successEvent, onFailure);
      socket.once(options.successEvent, onSuccess);
      for (const error of options.errorEvents || []) socket.once(error, onFailure);
      this.send(event, args, options.requireMembership);
    });
  }

  /** Retains the socket and all subscriptions for foreground recovery. */
  suspend() {
    this.joined = false;
    this.stopHeartbeat();
    this.cancelReconnect();
    this.cancelPending();
    this.socket?.disconnect();
  }

  resume() {
    this.suspend();
    this.socket?.connect();
  }

  /** Terminal teardown. Subscribe again to the new socket if connect() is called later. */
  disconnect() {
    this.suspend();
    this.socket?.removeAllListeners();
    this.socket = null;
  }

  private cancelPending() {
    for (const cancel of [...this.pending.values()]) cancel();
  }

  private cancelReconnect() {
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
  }

  private stopHeartbeat() {
    clearInterval(this.heartbeatInterval);
    clearTimeout(this.heartbeatTimeout);
    this.heartbeatInterval = undefined;
    this.heartbeatTimeout = undefined;
  }

  private startHeartbeat() {
    this.stopHeartbeat();
    const heartbeat = this.options.heartbeat;
    if (!heartbeat) return;
    this.heartbeatInterval = setInterval(() => {
      if (!this.socket?.connected) return;
      // Register timeout first, including when an injected transport responds synchronously.
      this.heartbeatTimeout = setTimeout(() => {
        this.heartbeatTimeout = undefined;
        this.missedHeartbeats++;
        this.options.log?.('Missed heartbeat', { count: this.missedHeartbeats, max: heartbeat.maxMissed });
        if (this.missedHeartbeats >= heartbeat.maxMissed) {
          this.suspend();
          this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = undefined;
            this.socket?.connect();
          }, 1000);
        }
      }, heartbeat.timeoutMs);
      this.socket.emit(heartbeat.ping);
    }, heartbeat.intervalMs);
  }
}
